// Device-local receiver directory (spec.nfc-pwa §6, spec.security-privacy §3):
// cards a Receiver has collected live ONLY in their browser (IndexedDB), keyed
// by publicId with a local timestamp. No account, no server-side PII. This is
// what lets the collected list work fully offline.
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/** One labelled way to reach someone — "Mobile", "+14155550142". */
export interface Contact { label?: string; value: string }

/** The company behind a card, as a recipient sees it. `verified` is true only
 *  when the company proved its domain AND this card's address is on it. */
export interface CardOrg {
  name: string;
  verified: boolean;
  /** The event a GUEST badge belongs to. Mutually exclusive with verified —
   *  a badge asserts attendance, never employment. */
  event?: string;
  logo?: string; theme?: string;
  footer?: string; address?: string;
  sharedContacts?: Contact[];
}

/** One labelled destination behind the card front. */
export interface ExtraLink { label: string; url: string }

/** A single marketing block. One, not a list — a card with three competing
 *  offers has none. */
export interface ExtraPromo { headline?: string; body?: string; cta?: string; url?: string }

/** Everything behind the front of a card. Server-validated against a closed
 *  schema, so these strings are safe to render as text and hrefs. */
export interface CardExtras { links?: ExtraLink[]; promo?: ExtraPromo }

/** The public projection returned by GET /api/v1/profiles/:publicId. */
export interface PublicProfile {
  publicId: string;
  name: string;
  title?: string;
  /** Which department, on a company card (org roster supplied). */
  department?: string;
  company?: string;
  phone?: string;
  email?: string;
  address?: string;
  /** Additional ways to reach someone, beyond phone/email above. Never on the
   *  card front (its grid is fixed); shown in the expanded view. */
  extraPhones?: Contact[];
  extraEmails?: Contact[];
  /** Industry slug — used to sort a large collected directory. */
  industry?: string;
  socials?: Record<string, string>;
  styling?: Record<string, string>;
  /** Named --card-* preset the owner designed the card with. */
  theme?: string;
  /** Owner's headshot, inlined so a collected card still renders offline. */
  photoDataUrl?: string;
  /** A picture that replaces the card's designed back. */
  backImageDataUrl?: string;
  /** Content behind the card's front: labelled links and a promo block. The
   *  FRONT never changes because of this — it stays the standard contact card.
   *  Cached with the projection so it renders offline. */
  extras?: CardExtras;
  /** The company behind this card, when there is one. */
  org?: CardOrg;
  /** Whether the card has been claimed by its owner. Drives whether the app
   *  offers to send them an invitation. Never says who the owner is. */
  claimed?: boolean;
  /** A company-issued card its holder has not claimed yet. The server sends
   *  the id and this flag only, so nothing else on the projection is populated:
   *  a roster upload must not publish an employer's staff before those people
   *  have agreed to it. Such a card is never worth collecting — there is
   *  nothing on it to collect. */
  pending?: boolean;
}

/** A collected card = the projection + when it was collected + what YOU wrote
 *  about it.
 *
 *  `note` is yours, not theirs. It never leaves the device, is never sent to the
 *  server, and the card's owner cannot see it — a card is a copy of what
 *  somebody published, and what you remember about meeting them is a different
 *  thing that happens to be stored beside it. */
export interface CollectedCard extends PublicProfile {
  collectedAt: number;
  /** When this copy was last checked against the server. */
  refreshedAt?: number;
  /** Private, device-local note: where you met, what to follow up. */
  note?: string;
  /** When the note was last written. */
  notedAt?: number;
  /** Your own labels for this card. Prose is what a note is for; these are what
   *  a filter can be built from. Device-local, like the note. */
  tags?: string[];
}

/** Fields that belong to the DEVICE, not to the published card.
 *
 *  They must survive every refresh: a refresh replaces the stored copy with what
 *  the owner has since published, and anything local that is not carried across
 *  is destroyed silently, six hours after it was written. */
const LOCAL_FIELDS = ['note', 'notedAt', 'tags'] as const;

const DB_NAME = 'smartcard';
const STORE = 'collected';

interface SmartcardDB extends DBSchema {
  collected: {
    key: string;
    value: CollectedCard;
    indexes: { 'by-collectedAt': number };
  };
}

let dbP: Promise<IDBPDatabase<SmartcardDB>> | null = null;

function db(): Promise<IDBPDatabase<SmartcardDB>> {
  if (!dbP) {
    dbP = openDB<SmartcardDB>(DB_NAME, 1, {
      upgrade(database) {
        const store = database.createObjectStore(STORE, { keyPath: 'publicId' });
        store.createIndex('by-collectedAt', 'collectedAt');
      },
    });
  }
  return dbP;
}

/**
 * Persist a collected card, or update the copy already held.
 *
 * `collectedAt` is when you FIRST took the card and must survive an update —
 * it is what the directory orders by, so restamping it on every refresh would
 * shuffle somebody's whole collection each time a card of theirs changed.
 */
export async function saveCollected(profile: PublicProfile): Promise<CollectedCard> {
  const database = await db();
  const now = Date.now();
  const existing = profile.publicId
    ? await database.get(STORE, profile.publicId)
    : undefined;
  const card: CollectedCard = {
    ...profile,
    collectedAt: existing?.collectedAt ?? now,
    refreshedAt: now,
  };
  // Carry the device's own fields across. `profile` is what the OWNER published
  // and knows nothing about them, so spreading it over the stored card would
  // erase a note the moment its author's card was refreshed — which is a
  // background job, so the loss would be silent and blamed on nothing.
  if (existing) {
    for (const field of LOCAL_FIELDS) {
      if (existing[field] !== undefined) {
        (card as unknown as Record<string, unknown>)[field] = existing[field];
      }
    }
  }
  await database.put(STORE, card);
  return card;
}

/**
 * Bring collected cards up to date with what their owners have published.
 *
 * A collected card is a copy taken at the moment it was tapped. Without this it
 * stays that way for ever: somebody changes job, republishes, and every person
 * holding their card still sees the old title — which defeats the point of the
 * card being digital at all.
 *
 * Not realtime, and it does not need to be. A card changes rarely and is looked
 * at rarely; checking when the app opens is the cheapest thing that keeps it
 * honest. `maxAge` stops a rapid reopen from re-fetching everything.
 *
 * A card that fails to fetch KEEPS its stored copy. Being offline, or a card
 * that has been taken out of service, must not empty someone's directory.
 */
export async function refreshCollected(
  fetchMany: (publicIds: string[]) => Promise<PublicProfile[]>,
  opts: { maxAge?: number; limit?: number; now?: number } = {},
): Promise<{ checked: number; updated: number }> {
  // Six hours, not minutes. A business card changes when somebody changes job;
  // checking more often than that spends the server's time to learn nothing.
  const maxAge = opts.maxAge ?? 6 * 60 * 60 * 1000;
  // One request, bounded. A collection larger than this catches up over the
  // next few launches, oldest first, instead of in one burst.
  const limit = opts.limit ?? 50;
  const now = opts.now ?? Date.now();

  const stale = (await listCollected())
    .filter((c) => !!c.publicId && now - (c.refreshedAt ?? c.collectedAt ?? 0) >= maxAge)
    .sort((a, b) => (a.refreshedAt ?? a.collectedAt) - (b.refreshedAt ?? b.collectedAt))
    .slice(0, limit);
  if (!stale.length) return { checked: 0, updated: 0 };

  let updated = 0;
  try {
    const fresh = await fetchMany(stale.map((c) => c.publicId!));
    for (const profile of fresh) {
      await saveCollected(profile);
      updated++;
    }
    // Cards that did not come back are gone or unreachable. Their stored copy
    // stands — being offline, or holding a card taken out of service, must not
    // empty somebody's directory — but the clock is reset either way, so a card
    // that no longer resolves is not retried on every single launch.
    const returned = new Set(fresh.map((f) => f.publicId));
    for (const c of stale) {
      if (!returned.has(c.publicId)) await touchCollected(c.publicId!, now);
    }
  } catch {
    /* offline — nothing changes, and it will be tried again next launch */
  }
  return { checked: stale.length, updated };
}

/** Mark a card as checked without altering it. */
async function touchCollected(publicId: string, now: number): Promise<void> {
  const database = await db();
  const existing = await database.get(STORE, publicId);
  if (existing) await database.put(STORE, { ...existing, refreshedAt: now });
}

/** At most this many tags on one card, and this long each. Bounds exist because
 *  these are typed by hand into a filter row that has to stay readable. */
export const MAX_TAGS = 12;
export const MAX_TAG_LENGTH = 32;

/**
 * Clean a list of typed tags.
 *
 * Trimmed, emptied of blanks, capped, and de-duplicated CASE-INSENSITIVELY while
 * keeping the casing first written — somebody who types "Expo" and later "expo"
 * means one tag, and showing them both in a filter row as separate chips would
 * be the app disagreeing with them about what they just did.
 */
export function normalizeTags(raw: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const tag = item.trim().replace(/\s+/g, ' ').slice(0, MAX_TAG_LENGTH);
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
    if (out.length >= MAX_TAGS) break;
  }
  return out;
}

/** Split what somebody typed into a tag input. Comma-separated, because that is
 *  what people type without being told. */
export function parseTags(input: string): string[] {
  return normalizeTags(input.split(','));
}

/**
 * Write your private note and/or tags on a collected card.
 *
 * A PATCH: only the keys present are touched, so writing tags cannot silently
 * blank a note. An empty string or empty array CLEARS that field.
 *
 * Returns the updated card, or null when there is no such card — annotating
 * must never CREATE one, or a stale id would leave a note attached to a card
 * nobody holds.
 */
export async function annotateCollected(
  publicId: string,
  patch: { note?: string; tags?: string[] },
): Promise<CollectedCard | null> {
  const database = await db();
  const existing = await database.get(STORE, publicId);
  if (!existing) return null;
  const card: CollectedCard = { ...existing };

  if (patch.note !== undefined) {
    const trimmed = patch.note.trim();
    if (trimmed) {
      card.note = trimmed;
      card.notedAt = Date.now();
    } else {
      // Clearing removes the field rather than storing an empty string, so
      // "has a note" stays a single unambiguous check everywhere.
      delete card.note;
      delete card.notedAt;
    }
  }
  if (patch.tags !== undefined) {
    const tags = normalizeTags(patch.tags);
    if (tags.length) card.tags = tags;
    else delete card.tags;
  }

  await database.put(STORE, card);
  return card;
}

/** Every tag in use across a collection, most-used first then alphabetical.
 *  Ordered by use because a filter row should lead with what it will actually
 *  filter, and stably so it does not reshuffle as cards are collected. */
export function tagsIn(cards: CollectedCard[]): { tag: string; count: number }[] {
  const counts = new Map<string, { tag: string; count: number }>();
  for (const card of cards) {
    for (const tag of card.tags ?? []) {
      const key = tag.toLowerCase();
      const hit = counts.get(key);
      if (hit) hit.count++;
      else counts.set(key, { tag, count: 1 });
    }
  }
  return [...counts.values()].sort((a, b) =>
    b.count - a.count || a.tag.localeCompare(b.tag));
}

/** Does this card carry the given tag? Case-insensitive, like everything else
 *  a person types. */
export function hasTag(card: CollectedCard, tag: string): boolean {
  const want = tag.trim().toLowerCase();
  if (!want) return true;
  return (card.tags ?? []).some((t) => t.toLowerCase() === want);
}

/** All collected cards, newest first. Reads only from IndexedDB (works offline). */
export async function listCollected(): Promise<CollectedCard[]> {
  const database = await db();
  const all = await database.getAllFromIndex(STORE, 'by-collectedAt');
  return all.reverse();
}

/** Case-insensitive full-text filter over name/title/company AND your own note
 *  (client-side, offline).
 *
 *  The note is searchable because it is usually the only place the thing you
 *  actually remember is written down — "met at the Dhaka expo" is how somebody
 *  looks for a card whose name they have forgotten, which is the whole reason
 *  for keeping one. */
export function searchCollected(cards: CollectedCard[], query: string): CollectedCard[] {
  const q = query.trim().toLowerCase();
  if (!q) return cards;
  return cards.filter((c) =>
    [c.name, c.title, c.company, c.note, ...(c.tags ?? [])]
      .some((v) => (v || '').toLowerCase().includes(q)),
  );
}

/** Test/utility: clear the store. */
export async function clearCollected(): Promise<void> {
  const database = await db();
  await database.clear(STORE);
}
